'use client'

import { useCallback, useId, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { ActionIcon, Avatar, Box, Button, Group, Slider, Stack, Text, Tooltip } from '@mantine/core'
import { IMAGE_MIME_TYPE } from '@mantine/dropzone'
import Cropper, { Area } from 'react-easy-crop'
import { LuTrash2, LuUserRound } from 'react-icons/lu'
import { cropImageToDataUrl } from '@lib/utils/crop-image'

import classes from './profile-image-input.module.css'

interface ProfileImageInputProps {
    value?: string | null
    onChange: (value: string | undefined) => void
    onRemove?: () => void
    description?: string
    /** When false, the helper line above the control is omitted (minimal flows). Default true. */
    showDescription?: boolean
}

const MAX_FILE_SIZE = 5 * 1024 * 1024
/** Matches `--profile-avatar-size` in profile-image-input.module.css (17.5rem ≈ 280px). */
const PROFILE_AVATAR_SIZE_PX = 280
const PLACEHOLDER_ICON_SIZE = 96

export default function ProfileImageInput({
    value,
    onChange,
    onRemove,
    description,
    showDescription = true
}: ProfileImageInputProps) {
    const t = useTranslations('userSettings.profileImage')
    const inputId = useId()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [sourceUrl, setSourceUrl] = useState<string | null>(null)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
    const [saving, setSaving] = useState(false)
    const [fileError, setFileError] = useState<string | null>(null)

    const handleFileSelected = (file: File | undefined) => {
        if (!file) return

        if (!IMAGE_MIME_TYPE.includes(file.type as (typeof IMAGE_MIME_TYPE)[number])) {
            setFileError(t('fileTypeError'))
            return
        }
        if (file.size > MAX_FILE_SIZE) {
            setFileError(t('fileTooLarge'))
            return
        }

        setFileError(null)
        setSourceUrl(URL.createObjectURL(file))
        setCrop({ x: 0, y: 0 })
        setZoom(1)
    }

    const onCropComplete = useCallback((_: Area, pixels: Area) => {
        setCroppedAreaPixels(pixels)
    }, [])

    const handleSave = async () => {
        if (!sourceUrl || !croppedAreaPixels) return
        setSaving(true)
        try {
            const dataUrl = await cropImageToDataUrl(sourceUrl, croppedAreaPixels, PROFILE_AVATAR_SIZE_PX)
            onChange(dataUrl)
            URL.revokeObjectURL(sourceUrl)
            setSourceUrl(null)
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        if (sourceUrl) URL.revokeObjectURL(sourceUrl)
        setSourceUrl(null)
    }

    const handleRemove = () => {
        onChange(undefined)
        onRemove?.()
    }

    const avatarAriaLabel = value ? t('changePhotoAria') : t('addPhotoAria')

    return (
        <Stack className={classes.root} gap='lg' align='center'>
            {showDescription ? (
                <Text c='dimmed' size='md' ta='center'>
                    {description ?? t('defaultDescription')}
                </Text>
            ) : null}

            {sourceUrl ? (
                <Stack gap='md' className={classes.cropBlock}>
                    <Text component='p' className={classes.cropTitle}>
                        {t('adjustTitle')}
                    </Text>
                    <Box className={classes.cropFrame}>
                        <Cropper
                            image={sourceUrl}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape='round'
                            showGrid={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    </Box>
                    <Group className={classes.cropControls} gap='md' align='center' wrap='nowrap'>
                        <Text size='sm' w={48} fw={500}>
                            {t('zoom')}
                        </Text>
                        <Slider
                            flex={1}
                            miw={0}
                            size='md'
                            min={1}
                            max={3}
                            step={0.01}
                            value={zoom}
                            onChange={setZoom}
                            label={null}
                        />
                    </Group>
                    <Group justify='center' gap='sm' wrap='wrap'>
                        <Button variant='default' size='md' onClick={handleCancel} disabled={saving}>
                            {t('cancel')}
                        </Button>
                        <Button variant='default' size='md' onClick={handleSave} loading={saving}>
                            {t('setProfilePhoto')}
                        </Button>
                    </Group>
                </Stack>
            ) : (
                <Box className={classes.avatarBlock}>
                    <label htmlFor={inputId} className={classes.avatarTrigger} aria-label={avatarAriaLabel}>
                        <Avatar
                            src={value ?? undefined}
                            size={PROFILE_AVATAR_SIZE_PX}
                            radius='50%'
                            classNames={{
                                root: classes.avatarRoot,
                                image: classes.avatarImage,
                                placeholder: classes.avatarPlaceholder
                            }}>
                            <LuUserRound size={PLACEHOLDER_ICON_SIZE} strokeWidth={1.25} aria-hidden />
                        </Avatar>
                    </label>

                    <p className={classes.hint}>{value ? t('changeHint') : t('addHint')}</p>

                    {value ? (
                        <Tooltip label={t('removePhoto')} withArrow position='bottom'>
                            <ActionIcon
                                variant='subtle'
                                color='gray'
                                size='lg'
                                radius='xl'
                                classNames={{ root: classes.removeButton }}
                                onClick={handleRemove}
                                aria-label={t('removePhotoAria')}>
                                <LuTrash2 size={18} aria-hidden />
                            </ActionIcon>
                        </Tooltip>
                    ) : null}

                    {fileError ? (
                        <Text role='alert' size='sm' c='red' ta='center'>
                            {fileError}
                        </Text>
                    ) : null}
                </Box>
            )}

            <input
                ref={fileInputRef}
                id={inputId}
                type='file'
                accept={IMAGE_MIME_TYPE.join(',')}
                hidden
                onChange={(e) => {
                    handleFileSelected(e.currentTarget.files?.[0])
                    e.currentTarget.value = ''
                }}
            />
        </Stack>
    )
}
